/**
 * Which attribute a Product Card Grid card highlights, as one answer for both
 * sides of the engine (CMS-1890 review).
 *
 * The card is authored with a `SystemAttribute` reference, and the two fields
 * on it are not interchangeable: an editor may pick the attribute by name, or
 * the reference may carry a `uid` alone. The renderer already resolved both —
 * uid first against the product's own attributes, then the authored name — and
 * the collector reported only the name. A card authored by uid alone was then
 * left out of the request while still being rendered through the engine, so on
 * a page where another card armed the calculation its metric went blank
 * instead of showing the authored figure.
 *
 * Both sides now ask here. The inputs are declared structurally rather than as
 * `CmsProduct` / `SystemAttribute` so the collector, which deliberately names
 * no mapper type, can call it with its own minimal shape.
 */

/** The reference a card carries — `SystemAttribute` satisfies this. */
export type HighlightAttributeRef = {
  uid?: string | null;
  name?: string | null;
};

/** Only the part of a product this lookup reads. */
export type ProductWithAttributeNames = {
  attributes?: Array<{
    name?: { uid?: string | null; name?: string | null } | null;
  } | null> | null;
};

/**
 * The product attribute the card's reference points at, matched by `uid` when
 * the reference carries one and by `name` otherwise.
 */
export const findHighlightedProductAttribute = <
  A extends { name?: { uid?: string | null; name?: string | null } | null },
>(
  highlightTextAttribute: HighlightAttributeRef | null | undefined,
  attributes: Array<A | null | undefined> | null | undefined,
): A | undefined =>
  attributes?.find((attribute): attribute is A => {
    if (!attribute) return false;
    if (highlightTextAttribute?.uid) {
      return attribute.name?.uid === highlightTextAttribute.uid;
    }
    if (highlightTextAttribute?.name) {
      return attribute.name?.name === highlightTextAttribute.name;
    }
    return false;
  }) ?? undefined;

/**
 * The attribute NAME the card's metric resolves to — what the engine is asked
 * for. The editor's chosen name wins; the matched catalogue entry stands in
 * when the card was authored by uid alone.
 */
export const resolveHighlightAttributeName = (
  highlightTextAttribute: HighlightAttributeRef | null | undefined,
  product: ProductWithAttributeNames | null | undefined,
): string | undefined =>
  highlightTextAttribute?.name ??
  findHighlightedProductAttribute(highlightTextAttribute, product?.attributes)
    ?.name?.name ??
  undefined;
